<script setup lang="ts">
import { ref, onMounted } from "vue";
import { useSearchStore } from "@/stores/search";
import type SearchFunnelEvent from "@/types/funnel/searchFunnelEvent";
import { SEARCH } from "@/globalConsts/searchFunnelEvents";
import { t } from "@/services/i18n";
import { pushToDataLayer } from "@/lib/gtm/dataLayer";

const props = defineProps({
    currentValue: {
        type: String,
        default: "",
    },
});
const store = useSearchStore();

const { quickSearch, resetQuickSearchResults, setResults } = store;
const searchTerm = ref<string>(props.currentValue);
const inputIsFocus = ref(false);
const quickSearchContent = ref<HTMLElement>();
const quickSearchContentHeight = ref();
async function setQuickSearchContentHeight() {
    quickSearchContentHeight.value =
        quickSearchContent.value?.offsetHeight + "px";
    const root = document.documentElement;
    root.style.setProperty(
        "--quick-search-content-height",
        quickSearchContentHeight.value
    );
}

async function search() {
    pushToDataLayer({
        event: SEARCH,
        search_term: props.currentValue,
    } as SearchFunnelEvent);
    await quickSearch(searchTerm.value);
    searchTerm.value = props.currentValue;
}

async function remove() {
    searchTerm.value = "";
    resetQuickSearchResults();

    await setResults();
}

onMounted(async () => {
    searchTerm.value = props.currentValue;
    await setQuickSearchContentHeight();
});
</script>
<template>
    <div class="[ c-quicksearch ]">
        <div
            ref="quickSearchContent"
            class="[ c-quicksearch__content ]"
            :class="inputIsFocus ? 'is-focus-quicksearch' : ''"
        >
            <div
                class="[ c-quicksearch__box ]"
                :class="{ 'is-filled': inputIsFocus || searchTerm.length > 0 }"
            >
                <label
                    v-show="searchTerm.length > -1"
                    class="[ c-quicksearch__label ]"
                    for="destination_search"
                >
                    {{ t("where") }}
                </label>
                <input
                    id="destination_search"
                    v-model="searchTerm"
                    name="search"
                    class="[ c-quicksearch__input ]"
                    type="text"
                    tabindex="0"
                    :placeholder="t('where')"
                    @keydown.enter="search"
                    @focusin="inputIsFocus = true"
                    @focusout="inputIsFocus = false"
                />
            </div>
            <button
                v-if="searchTerm"
                class="[ c-quicksearch__reset ]"
                @click="remove"
            >
                <i class="[ c-icon icon-close c-icon--medium ]"></i>
            </button>
            <button
                class="[ c-quicksearch__button ]"
                @click="search"
                type="submit"
                aria-label="Search"
            >
                <i class="[ c-icon icon-search c-icon--large ]"></i>
            </button>
        </div>
    </div>
</template>
